import { Star } from "lucide-react";
import { BarberPhoto } from "@/components/BarberPhoto";
import { languagesLine } from "@/data/barbers";

type Barber = {
  name: string;
  photo?: string;
  rating?: number;
  reviewCount?: number;
  bio?: string;
  photoZoom?: number;
  photoYOffset?: string;
  bookingUrl?: string;
  alsoAt?: readonly string[];
  languages?: string[];
};

/**
 * Build a descriptive, SEO-friendly alt text for a barber portrait.
 * Shape: "<Name>, barberare på Tailor's Barber <Salon> – <short descriptor>".
 * The descriptor is derived from the bio by stripping the "Name är …" prefix
 * and taking the first clause, with redundant salon/location words removed.
 */
function barberAlt(b: Barber, salonName: string): string {
  const base = `${b.name}, barberare på Tailor's Barber ${salonName}`;
  if (!b.bio) return base;

  // Strip the leading "<Name> är [en|ett|en av|ett av] " prefix.
  let rest = b.bio.startsWith(b.name) ? b.bio.slice(b.name.length) : b.bio;
  const m = rest.match(/^\s*är\s+(?:en|ett|en av|ett av)?\s*(.*)$/i);
  let desc = m ? m[1] : rest.trim();

  // Keep only the first clause (up to a dash or comma).
  desc = desc.split(/\s+[–-]\s|,/)[0].trim();

  // Remove redundant salon/location mentions already covered by `base`.
  desc = desc
    .replace(/\b(?:i\s+)?(?:Täby Centrum|Åhléns City|Arninge|Åkersberga|Stockholm|Tailor's Barber)\b/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();

  // Tidy leading connectives / filler.
  desc = desc.replace(/^(?:som|med|känd|en av|ett av|av)\s+/i, "").trim();

  if (!desc) return base;
  return `${base} – ${desc}`.slice(0, 150);
}

interface BarberGridProps {
  barbers: readonly Barber[];
  salonName: string;
}

export function BarberGrid({ barbers, salonName }: BarberGridProps) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {barbers.filter((b) => b.photo).map((b) => {
        const inner = (
          <>
            <div
              className="relative w-[80px] h-[110px] md:w-[90px] md:h-[120px] rounded-xl overflow-hidden flex-shrink-0"
              style={{ backgroundColor: "#F5F2ED" }}
            >
              <BarberPhoto
                src={b.photo!}
                alt={barberAlt(b, salonName)}
                zoom={b.photoZoom}
                yOffset={b.photoYOffset}
              />
            </div>
            <div className="flex flex-col justify-center min-w-0">
              <h3 className="font-medium text-base">{b.name}</h3>
              {typeof b.rating === "number" && (
                <div className="flex items-center gap-1 text-sm mt-0.5">
                  <Star size={14} className="fill-brass text-brass" />
                  <span className="font-bold">{b.rating.toFixed(1).replace(".", ",")}</span>
                  {typeof b.reviewCount === "number" && (
                    <span className="text-muted-foreground">
                      · {b.reviewCount.toLocaleString("sv-SE")} omdömen
                    </span>
                  )}
                </div>
              )}
              {b.bio && (
                <p className="text-sm text-muted-foreground mt-1">{b.bio}</p>
              )}
              {b.languages && b.languages.length > 0 && (
                <p className="text-xs text-muted-foreground mt-1">{languagesLine(b.languages)}</p>
              )}
              {b.alsoAt && b.alsoAt.length > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  Jobbar även i {b.alsoAt.join(" och ")}
                </p>
              )}
              {b.bookingUrl && (
                <span className="mt-2 text-sm font-medium text-brass-deep underline underline-offset-2">
                  Boka {b.name} →
                </span>
              )}
            </div>
          </>
        );

        const cls =
          "card-elev p-3 md:p-4 flex flex-row items-center gap-4 hover:border-brass/50 transition-colors";

        return b.bookingUrl ? (
          <a
            key={b.name}
            href={b.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Boka tid hos ${b.name} på Tailor's Barber ${salonName} via BokaDirekt`}
            className={cls}
          >
            {inner}
          </a>
        ) : (
          <article key={b.name} className={cls}>
            {inner}
          </article>
        );
      })}
    </div>
  );
}
